import type { Segment } from '@/features/segment/segment'
import { LineRichMenuStatus, TargetType } from '@/types/schema.generated'
import { segmentSchema } from '@/features/segment/components/SegmentForm/formSchema'
import * as v from 'valibot'

export type LineRichMenuFormType = {
  label: string
  status: LineRichMenuFormStatusType
  templateName: LineRichMenuFormTemplateName
  backgroundImage: LineRichMenuFormBackgroundImage | null
  templateActions: LineRichMenuFormTemplateActionType[]
  chatBarText: string
  isDisplayDefault: boolean
  targetType: TargetType
  segment:
    | {
        type: 'segment'
        segment: Segment
      }
    | undefined
}

export const LineRichMenuFormStatusPublished = 'published'
export const LineRichMenuFormStatusUnpublished = 'unpublished'
export type LineRichMenuFormStatusType =
  | typeof LineRichMenuFormStatusPublished
  | typeof LineRichMenuFormStatusUnpublished

export type LineRichMenuFormTemplateActionType = {
  panelName: string
  actionType: LineRichMenuFormActionType
  url: string
  label: string
  text: string
}

export const LineRichMenuFormActionTypeLink = 'link'
export const LineRichMenuFormActionTypeText = 'text'
export const LineRichMenuFormActionTypeUnselected = 'unselected'
export type LineRichMenuFormActionType =
  | typeof LineRichMenuFormActionTypeLink
  | typeof LineRichMenuFormActionTypeText
  | typeof LineRichMenuFormActionTypeUnselected

export type LineRichMenuFormBackgroundImage = {
  name: string | null
  image: File | string // 続きから編集するときは string になる
  size: 'small' | 'large'
}

export type LineRichMenuFormTemplateName =
  | (typeof largeTemplateNames)[number]
  | (typeof smallTemplateNames)[number]

export const largeTemplateNames = [
  'large_201',
  'large_202',
  'large_203',
  'large_204',
  'large_205',
  'large_206',
  'large_207',
] as const

export const smallTemplateNames = [
  'small_101',
  'small_102',
  'small_103',
  'small_104',
  'small_105',
] as const

const labelSchema = v.pipe(
  v.string(),
  v.trim(),
  v.nonEmpty('必須です'),
  v.maxLength(30, '30文字以内で入力してください')
)

const statusSchema = v.enum({
  published: LineRichMenuStatus.Published,
  unpublished: LineRichMenuStatus.Unpublished,
})

const publishedActionTypeSchema = v.enum(
  {
    link: LineRichMenuFormActionTypeLink,
    text: LineRichMenuFormActionTypeText,
  },
  '公開ステータスの場合はリンクまたはテキストを選択してください'
)

const unpublishedActionTypeSchema = v.enum({
  link: LineRichMenuFormActionTypeLink,
  text: LineRichMenuFormActionTypeText,
  unselected: LineRichMenuFormActionTypeUnselected,
})

const urlSchema = v.optional(v.pipe(v.string(), v.trim()))

const actionLabelSchema = v.optional(
  v.pipe(v.string(), v.trim(), v.maxLength(20, '20文字以内で入力してください'))
)

const textSchema = v.optional(
  v.pipe(v.string(), v.trim(), v.maxLength(50, '50文字以内で入力してください'))
)

const chatBarTextSchema = v.pipe(
  v.string(),
  v.trim(),
  v.nonEmpty('必須です'),
  v.maxLength(14, '14文字以内で入力してください')
)

const targetTypeSchema = v.enum({
  allFriends: TargetType.AllFriends,
  allMatchedUsers: TargetType.AllMatchedUsers,
  segment: TargetType.Segment,
})

const publishedTemplateActionSchema = v.pipe(
  v.object({
    panelName: v.pipe(v.string(), v.trim(), v.nonEmpty()),
    actionType: publishedActionTypeSchema,
    url: urlSchema,
    label: actionLabelSchema,
    text: textSchema,
  }),
  v.forward(
    v.check(({ actionType, url }) => {
      if (actionType === LineRichMenuFormActionTypeLink) {
        return url !== undefined && url.length > 0
      }
      return true
    }, 'リンク選択時は必須です'),
    ['url']
  ),
  v.forward(
    v.check(({ actionType, label }) => {
      if (actionType === LineRichMenuFormActionTypeLink) {
        return label !== undefined && label.length > 0
      }
      return true
    }, 'リンク選択時は必須です'),
    ['label']
  ),
  v.forward(
    v.check(({ actionType, url }) => {
      if (actionType === LineRichMenuFormActionTypeLink) {
        if (url !== undefined)
          // https:// または http:// または line:// または tel: で始まるか
          return /^(https?|line):\/\/[^\s/$.?#].[^\s]*$|^tel:[0-9-]+$/.test(url)
      }
      return true
    }, 'URLの形式が正しくありません'),
    ['url']
  ),
  v.forward(
    v.check(({ actionType, text }) => {
      if (actionType === LineRichMenuFormActionTypeText) {
        return text !== undefined && text.length > 0
      }
      return true
    }, 'テキスト選択時は必須です'),
    ['text']
  )
)

const unpublishedTemplateActionSchema = v.pipe(
  v.object({
    panelName: v.pipe(v.string(), v.trim(), v.nonEmpty()),
    actionType: unpublishedActionTypeSchema,
    url: urlSchema,
    label: actionLabelSchema,
    text: textSchema,
  }),
  v.forward(
    v.check(({ actionType, url }) => {
      if (actionType === LineRichMenuFormActionTypeLink) {
        return url !== undefined && url.length > 0
      }
      return true
    }, 'リンク選択時は必須です'),
    ['url']
  ),
  v.forward(
    v.check(({ actionType, label }) => {
      if (actionType === LineRichMenuFormActionTypeLink) {
        return label !== undefined && label.length > 0
      }
      return true
    }, 'リンク選択時は必須です'),
    ['label']
  ),
  v.forward(
    v.check(({ actionType, text }) => {
      if (actionType === LineRichMenuFormActionTypeText) {
        return text !== undefined && text.length > 0
      }
      return true
    }, 'テキスト選択時は必須です'),
    ['text']
  )
)

export const validPublishedLineRichMenuFormSchema = v.pipe(
  v.object({
    label: labelSchema,
    status: statusSchema,
    template: v.pipe(v.string(), v.trim(), v.nonEmpty()),
    backgroundImageFileName: v.pipe(
      v.string(),
      v.trim(),
      v.nonEmpty('公開ステータスでは画像は必須です')
    ),
    templateActions: v.pipe(
      v.array(publishedTemplateActionSchema),
      v.filterItems(item => {
        switch (item.actionType) {
          case LineRichMenuFormActionTypeLink:
            return !!item.url && !!item.label
          case LineRichMenuFormActionTypeText:
            return !!item.text
          default:
            return true
        }
      })
    ),
    chatBarText: chatBarTextSchema,
    isDisplayDefault: v.boolean(),
    targetType: targetTypeSchema,
    segment: v.optional(segmentSchema),
  }),
  v.forward(
    v.check(({ targetType, segment }) => {
      if (targetType === TargetType.Segment) {
        const conditions = segment?.conditions
        if (conditions === undefined) {
          return false
        }
        // 公開ステータスの場合はセグメント条件が1つ以上設定されていなければならない
        if (conditions!.length === 0) {
          return false
        }
        return true
      }
      return true
    }, '条件が設定されていません'),
    ['segment']
  )
)

export const validUnpublishedLineRichMenuFormSchema = v.pipe(
  v.object({
    label: labelSchema,
    status: statusSchema,
    template: v.pipe(v.string(), v.trim(), v.nonEmpty()),
    backgroundImageFileName: v.pipe(v.string(), v.trim()),
    templateActions: v.array(unpublishedTemplateActionSchema),
    chatBarText: chatBarTextSchema,
    isDisplayDefault: v.boolean(),
    targetType: targetTypeSchema,
    segment: v.optional(segmentSchema),
  }),
  v.forward(
    v.check(({ targetType, segment }) => {
      if (targetType === TargetType.Segment) {
        return segment !== undefined
      }
      return true
    }, 'セグメントが設定されていません'),
    ['segment']
  )
)

// FIXME: lint の対応で含めたが実際は不要なはずなので後ほど削除したい
export const defaultLineRichMenuFormSchema = v.pipe(
  v.object({
    label: labelSchema,
    status: statusSchema,
    template: v.pipe(v.string(), v.trim(), v.nonEmpty()),
    backgroundImageFileName: v.pipe(v.string(), v.trim()),
    templateActions: v.array(
      v.object({
        panelName: v.pipe(v.string(), v.trim(), v.nonEmpty()),
        actionType: unpublishedActionTypeSchema,
        url: urlSchema,
        label: actionLabelSchema,
        text: textSchema,
      })
    ),
    chatBarText: chatBarTextSchema,
    isDisplayDefault: v.boolean(),
    targetType: targetTypeSchema,
    segment: v.optional(segmentSchema),
  }),
  v.forward(
    v.check(({ targetType, segment }) => {
      if (targetType === TargetType.Segment) {
        return segment !== undefined
      }
      return true
    }, 'セグメントが設定されていません'),
    ['segment']
  )
)

export type LineRichMenuFormErrors = v.FlatErrors<
  | typeof validPublishedLineRichMenuFormSchema
  | typeof validUnpublishedLineRichMenuFormSchema
  | typeof defaultLineRichMenuFormSchema
>

export type LineRichMenuFormErrorPath = keyof NonNullable<
  LineRichMenuFormErrors['nested']
>

export type LineRichMenuFormErrorManager = {
  get: (path: LineRichMenuFormErrorPath) => string | undefined
  getStartsWith: (path: string) => string | undefined
}
